import { TextFieldForm, nameRegExp } from 'web-library';
import ControlledAutocomplete from 'web-library/components/form-items/ControlledAutocomplete';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CandidateProfileResponseObject } from 'web-library/models/AccountManagementConfiguration';
import { UseFormReturn } from 'react-hook-form';
import { useStyles } from './CandidatePersonalParticulars';
import { fallbackDashString } from '../../../utils/StringUtils';
import ToggleDisplayEditFormItem from '../../../components/ToggleDisplayEditFormItem';
import { CreateEditCandidateSchemaData } from './CreateEditCandidateSchema';
import { useGetIExamMasterCode } from '../../../api-refactored/user/useGetIExamMasterCode';
import { IExamMasterCodeItem } from '../../../api/user/model';

const ID_TYPE_MENU_ITEMS = [
  { key: 'I', value: 'I', children: 'NRIC/FIN' },
  { key: 'P', value: 'P', children: 'Passport No.' },
];

export interface CandidateNameAndIdParticularsProps {
  candidateProfileDetails?: CandidateProfileResponseObject;
  formHook?: UseFormReturn<CreateEditCandidateSchemaData>;
  isEditState: boolean;
  setPrStatus: React.Dispatch<React.SetStateAction<IExamMasterCodeItem | undefined | {}>>;
  setCitizenshipType: React.Dispatch<React.SetStateAction<IExamMasterCodeItem | undefined | {}>>;
}

const CandidateNameAndIdParticulars = ({
  candidateProfileDetails,
  formHook,
  isEditState,
  setPrStatus,
  setCitizenshipType,
}: CandidateNameAndIdParticularsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { data: iExamsData } = useGetIExamMasterCode();

  const selectedIdType = formHook?.watch('IdType');

  const isFin =
    formHook?.watch('NricFin')?.[0] === 'F' ||
    formHook?.watch('NricFin')?.[0] === 'G' ||
    formHook?.watch('NricFin')?.[0] === 'M';

  const setPrStatusCitizenTypeToForeigner = useCallback(() => {
    const iExamsDataISCitizenshipCodeValue = iExamsData?.citizenship.find(
      (data) => data.codeValue === 'IS'
    );

    const prStatusForForeigner = {
      ...iExamsDataISCitizenshipCodeValue,
      codeValueDescription: 'FOREIGNER',
    };

    const iExamsDataAPrStatusCodeValue = iExamsData?.prStatus.find(
      (data) => data.codeValue === 'A'
    );
    formHook?.setValue('prStatus', iExamsDataAPrStatusCodeValue);
    formHook?.setValue('citizenship', iExamsDataISCitizenshipCodeValue);
    formHook?.trigger('prStatus');
    setPrStatus(prStatusForForeigner);

    setCitizenshipType(iExamsDataISCitizenshipCodeValue);
  }, [formHook, iExamsData?.citizenship, iExamsData?.prStatus, setCitizenshipType, setPrStatus]);

  const resetPrStatusCitizenType = useCallback(() => {
    formHook?.setValue('prStatus', undefined);
    formHook?.setValue('citizenship', undefined);
    setPrStatus({});
    setCitizenshipType({});
  }, [formHook, setCitizenshipType, setPrStatus]);

  const onChangeIdType = useCallback(
    (_e: any, idType: any) => {
      formHook?.setValue('IdType', idType?.key);
      formHook?.trigger('IdType');
      if (idType?.key === 'P') {
        setPrStatusCitizenTypeToForeigner();
      } else if (!isFin) {
        resetPrStatusCitizenType();
      }
    },
    [formHook, isFin, resetPrStatusCitizenType, setPrStatusCitizenTypeToForeigner]
  );

  useEffect(() => {
    if (isFin) {
      setPrStatusCitizenTypeToForeigner();
    } else if (!formHook?.watch('NricFin')?.length && selectedIdType !== 'P') {
      resetPrStatusCitizenType();
    }
  }, [
    formHook,
    iExamsData?.citizenship,
    iExamsData?.prStatus,
    isFin,
    resetPrStatusCitizenType,
    selectedIdType,
    setCitizenshipType,
    setPrStatus,
    setPrStatusCitizenTypeToForeigner,
  ]);

  return (
    <>
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.Name')}
        valueText={candidateProfileDetails?.name}
        containerClassName={isEditState ? classes.formElementContainer : classes.halfWidthLeftItem}
        EditComponent={
          formHook && (
            <TextFieldForm
              form={formHook}
              onChange={(e) => formHook.setValue('name', e.target.value.toUpperCase())}
              id="inputText-candidateProfile-name"
              label={t('admin->>UserAccountManagement.Name')}
              placeholder="Enter name"
              isRequired
              error={!!formHook.formState.errors.name}
              helperText={formHook.formState.errors.name?.message?.toString()}
              name="name"
              type="text"
              defaultValue={candidateProfileDetails?.name}
              fullWidth
              isInputTextUpperCase
            />
          )
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        allHide
        labelText={t('admin->>UserAccountManagement.HanyuPinyinName')}
        valueText={fallbackDashString(candidateProfileDetails?.hanyuPinyinName)}
        containerClassName={isEditState ? classes.formElementContainer : classes.halfWidthRightItem}
        EditComponent={
          formHook && (
            <TextFieldForm
              form={formHook}
              id="inputText-candidateProfile-hanyuPinyinName"
              label={t('admin->>UserAccountManagement.HanyuPinyinName')}
              placeholder="Enter hanyu pinyin name"
              error={!!formHook.formState.errors?.hanyupinyinName}
              helperText={formHook.formState.errors?.hanyupinyinName?.message?.toString()}
              name="hanyupinyinName"
              type="text"
              regExpToBlock={nameRegExp}
              defaultValue={candidateProfileDetails?.hanyuPinyinName}
              fullWidth
            />
          )
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.IdType')}
        valueText={candidateProfileDetails?.userIdType}
        containerClassName={classes.halfWidthLeftItem}
        EditComponent={
          formHook && (
            <ControlledAutocomplete<{key: string, value: string, children: string}, false, false, undefined>
              label={t('admin->>UserAccountManagement.IdType')}
              isRequired
              control={formHook.control}
              name="IdType"
              id="inputSelect-idType"
              error={!!formHook.formState.errors?.IdType}
              helperText={formHook.formState.errors?.IdType?.message?.toString()}
              options={ID_TYPE_MENU_ITEMS}
              defaultValue={
                candidateProfileDetails?.userIdType === 'NRIC/FIN'
                  ? ID_TYPE_MENU_ITEMS[0]
                  : ID_TYPE_MENU_ITEMS[1]
              }
              onChange={onChangeIdType}
              disabled={!!candidateProfileDetails?.userIdType}
              getOptionLabel={({ children }) => children}
              renderOption={(_, option)=>option.children}
              placeholder="Select ID Type"
              transform={{
                input: (selectedOption, options) =>
                  options.find((option) => option.value === selectedOption) ?? null,
                output: (selectedOption) => (selectedOption ? selectedOption.value : null),
              }}
              subLabel={t('profile->>CreateCandidateForm.IdHelpText2')}
              isSubLabelBelowLabel
              readOnly
            />
          )
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.NricFinPassportNo')}
        valueText={candidateProfileDetails?.identity}
        containerClassName={classes.halfWidthLeftItem}
        EditComponent={
          formHook && (
            <TextFieldForm
              form={formHook}
              className={classes.nricFinPassportNo}
              onChange={(e) => formHook.setValue('NricFin', e.target.value.toUpperCase())}
              id="inputText-candidateProfile-nricfinpassport"
              label={t('admin->>UserAccountManagement.NricFinPassportNo')}
              inputProps={{
                readOnly: !!candidateProfileDetails?.identity,
              }}
              isRequired
              error={!!formHook.formState.errors?.NricFin}
              helperText={formHook?.formState.errors?.NricFin?.message?.toString()}
              name="NricFin"
              type="text"
              defaultValue={candidateProfileDetails?.identity}
              fullWidth
            />
          )
        }
      />
    </>
  );
};

export default CandidateNameAndIdParticulars;
